/**
 * Select/Multiselect/Radio Editor
 * Handles configuration for select, multiselect, and radio controls
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';

interface OptionItem {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Options Editor Component
 * Inline editor for managing select/radio options
 */
function OptionsEditor({ options, onChange }: { options: OptionItem[]; onChange: (opts: OptionItem[]) => void }) {
  const handleChange = (index: number, field: keyof OptionItem, val: string | boolean) => {
    const updated = options.map((opt, i) =>
      i === index ? { ...opt, [field]: val } : opt,
    );
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...options, { value: `option_${options.length + 1}`, label: `Option ${options.length + 1}` }]);
  };

  const handleRemove = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  return (
    <div className="af-properties-tab__options-editor">
      {options.length === 0 && (
        <p className="af-properties-tab__options-empty">No options yet. Add one below.</p>
      )}
      {options.map((opt, i) => (
        <div key={i} className="af-properties-tab__option-row">
          <input
            type="text"
            value={opt.value}
            onChange={(e) => handleChange(i, 'value', e.target.value)}
            className="af-properties-tab__input af-properties-tab__option-value"
            placeholder="value"
            title="Option value (stored)"
          />
          <input
            type="text"
            value={opt.label}
            onChange={(e) => handleChange(i, 'label', e.target.value)}
            className="af-properties-tab__input af-properties-tab__option-label"
            placeholder="label"
            title="Option label (displayed)"
          />
          <label className="af-properties-tab__checkbox-label">
            <input
              type="checkbox"
              checked={opt.disabled || false}
              onChange={(e) => handleChange(i, 'disabled', e.target.checked)}
            />
            Disabled
          </label>
          <button
            onClick={() => handleRemove(i)}
            className="af-properties-tab__button af-properties-tab__button--danger"
            type="button"
          >
            Remove
          </button>
        </div>
      ))}
      <button
        onClick={handleAdd}
        className="af-properties-tab__button af-properties-tab__button--primary"
        type="button"
      >
        Add Option
      </button>
    </div>
  );
}

/**
 * Render function for select/multiselect/radio controls
 */
function renderSelectEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const t = control.type;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Control Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>Options</label>
        <OptionsEditor
          options={(control.config?.options as OptionItem[]) || []}
          onChange={(opts) => onConfigChange('options', opts)}
        />
      </div>

      {t === 'radio' && (
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Orientation
            <select
              value={(control.config?.orientation as string) || 'vertical'}
              onChange={(e) => onConfigChange('orientation', e.target.value)}
              className="af-properties-tab__select"
            >
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * SelectEditor - Handles select, multiselect, radio controls
 */
export const SelectEditor: PropertyEditor = {
  type: 'select',
  label: 'Select/Multiselect/Radio',
  description: 'Configure options for selection controls',
  render: renderSelectEditor,
};

/**
 * Multiselect Editor - Uses same implementation as select
 */
export const MultiselectEditor: PropertyEditor = {
  type: 'multiselect',
  label: 'Multiselect',
  description: 'Configure options for multiselect control',
  render: renderSelectEditor,
};

/**
 * Radio Editor - Uses same implementation as select
 */
export const RadioEditor: PropertyEditor = {
  type: 'radio',
  label: 'Radio',
  description: 'Configure options for radio button control',
  render: renderSelectEditor,
};
